<script setup lang="ts">
import type { EventVault } from "@/models"
import { computed } from "vue"
import { FreemiumBadge } from "@/components"
import { ButtonLink, Expandable, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"
import { SocialsBehavior } from "@/types"

const props = defineProps<{
  eventVault: EventVault
}>()

const { t } = useTranslation()

const FREEMIUM_UPLOAD_WINDOW_DAYS = 30

const maxPhotoLimit = computed(() => props.eventVault.maxPhotoLimit)

interface PlanFeature {
  key: string
  label: string
  included: boolean
  detail?: string
}

const features = computed<PlanFeature[]>(() => {
  const settings = props.eventVault.eventVaultSettings

  return [
    {
      key: "photo_uploads",
      label: t("freemium_plan_details.features.photo_uploads"),
      included: Boolean(maxPhotoLimit.value),
      detail: t("freemium_plan_details.features.photo_uploads_detail", { max: maxPhotoLimit.value }),
    },
    {
      key: "upload_window",
      label: t("freemium_plan_details.features.upload_window"),
      included: true,
      detail: t("freemium_plan_details.features.upload_window_detail", { days: FREEMIUM_UPLOAD_WINDOW_DAYS }),
    },
    {
      key: "unlimited_uploads",
      label: t("freemium_plan_details.features.unlimited_uploads"),
      included: Boolean(!maxPhotoLimit.value),
    },
    {
      key: "video_uploads",
      label: t("freemium_plan_details.features.video_uploads"),
      included: Boolean(settings?.videoEnabled),
    },
    {
      key: "event_albums",
      label: t("freemium_plan_details.features.event_albums"),
      included: Boolean(settings?.albumsEnabled),
    },
    {
      key: "custom_colors",
      label: t("freemium_plan_details.features.custom_colors"),
      included: Boolean(settings?.allowThemeEdit),
    },
    {
      key: "written_guestbook",
      label: t("freemium_plan_details.features.written_guestbook"),
      included: true,
    },
    {
      key: "video_guestbook",
      label: t("freemium_plan_details.features.video_guestbook"),
      included: Boolean(settings?.videoEnabled),
    },
    {
      key: "premium_canva_templates",
      label: t("freemium_plan_details.features.premium_canva_templates"),
      included: false,
    },
    {
      key: "digital_invitations_rsvp",
      label: t("freemium_plan_details.features.digital_invitations_rsvp"),
      included: Boolean(settings?.invitationEnabled !== undefined && settings?.invitationEnabled !== null),
    },
    {
      key: "extended_upload_window",
      label: t("freemium_plan_details.features.extended_upload_window"),
      included: false,
    },
    {
      key: "reactions_comments",
      label: t("freemium_plan_details.features.reactions_comments"),
      included: (settings?.socialsBehavior ?? SocialsBehavior.Disabled) !== SocialsBehavior.Disabled,
    },
    {
      key: "basic_canva_templates",
      label: t("freemium_plan_details.features.basic_canva_templates"),
      included: true,
    },
  ]
})

const includedFeatures = computed(() => features.value.filter(feature => feature.included))
const notIncludedFeatures = computed(() => features.value.filter(feature => !feature.included))

const upgradeUrl = computed(() => props.eventVault.upgradeUrl)
</script>

<template>
  <section
    class="rounded-xl border border-premium bg-premium/10 overflow-hidden"
    data-test="freemium-plan-details"
  >
    <Expandable :initial-open="false">
      <template #activator="{ toggle, isOpen }">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-premium px-4 py-3 text-[#072a47]">
          <div class="flex flex-col gap-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <h2 class="font-heading font-bold text-lg">
                {{ t("freemium_plan_details.title") }}
              </h2>
            </div>
            <p class="text-sm font-body">
              {{ t("freemium_plan_details.summary", { uploads: maxPhotoLimit, days: FREEMIUM_UPLOAD_WINDOW_DAYS }) }}
            </p>
            <button
              type="button"
              class="w-fit flex items-center gap-2 text-sm font-semibold text-[#072a47] cursor-pointer"
              data-test="freemium-plan-details-toggle"
              @click="toggle"
            >
              {{ isOpen ? t("freemium_plan_details.hide_details") : t("freemium_plan_details.more_details") }}
              <Icon
                icon="CircleChevronDown"
                size="sm"
                class="transition-transform duration-200"
                :class="{ 'rotate-180': isOpen }"
              />
            </button>
          </div>
          <ButtonLink
            v-if="upgradeUrl"
            class="shrink-0 w-full sm:w-auto"
            variant="tertiary"
            :href="upgradeUrl"
            data-test="freemium-plan-details-upgrade"
            prefix-icon="Crown"
          >
            {{ t("freemium_plan_details.upgrade_now") }}
          </ButtonLink>
        </div>
      </template>

      <div class="grid grid-cols-1 md:grid-cols-2">
        <div class="bg-white p-4">
          <h3 class="font-heading font-bold text-sm uppercase tracking-wide mb-3">
            {{ t("freemium_plan_details.what_you_get") }}
          </h3>
          <ul class="flex flex-col gap-2">
            <li
              v-for="feature in includedFeatures"
              :key="`included-${feature.key}`"
              class="flex items-start gap-2 text-sm md:text-base"
            >
              <Icon icon="Check" size="sm" class="shrink-0 text-green-600 mt-0.5" />
              <span>
                {{ feature.label }}
                <span v-if="feature.detail" class="text-gray-500">({{ feature.detail }})</span>
              </span>
            </li>
          </ul>
          <p v-if="includedFeatures.length === 0" class="text-sm">
            {{ t("freemium_plan_details.no_features_included") }}
          </p>
        </div>

        <div class="bg-white p-4 border-t border-premium md:border-t-0 md:border-l">
          <h3 class="font-heading font-bold text-sm uppercase tracking-wide mb-3">
            {{ t("freemium_plan_details.unlock_more") }}
          </h3>
          <ul class="flex flex-col gap-2">
            <li
              v-for="feature in notIncludedFeatures"
              :key="`excluded-${feature.key}`"
              class="flex items-start gap-2 text-sm md:text-base text-gray-500"
            >
              <FreemiumBadge variant="icon" />
              <span>{{ feature.label }}</span>
            </li>
          </ul>
          <p v-if="notIncludedFeatures.length === 0" class="text-sm">
            {{ t("freemium_plan_details.all_features_included") }}
          </p>
        </div>
      </div>
    </Expandable>
  </section>
</template>
